import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronsLeftRight,
  Lock,
  LogOut,
  ArrowLeftRight,
  Store,
  UserCircle,
  Settings,
} from 'lucide-react';
import { useAuth, useDarkMode } from '@halo/web-lib';
import { ModeColorSwitch } from './ModeColorSwitch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/Tooltip';

// Logos
import iconTextLogoDark from '../assets/logo/icon-text-logo-dark.svg';
import iconTextLogoLight from '../assets/logo/icon-text-logo-light.svg';
import iconLogo from '../assets/logo/icon-logo.svg';

// Nav icons — dark mode (default)
import dashboardIconDark from '../assets/icons/dashboardIconDark.svg';
import askHaloIconDark from '../assets/icons/askHaloIconDark.svg';
import ordersIconDarkNew from '../assets/icons/ordersIconDarkNew.svg';
import campaignsHubIconDarkNew from '../assets/icons/campaignsHubIconDarkNew.svg';
import haloMMMIconDark from '../assets/icons/haloMMMIconDark.svg';
import myIntegrationsIcon from '../assets/icons/myIntegrationsIcon.svg';

// Nav icons — light mode
import dashboardIconLight from '../assets/icons/dashboardIconLight.svg';
import askHaloIconLight from '../assets/icons/askHaloIconLight.svg';
import ordersIconDark from '../assets/icons/ordersIconDark.svg';
import campaignsHubIconLightNew from '../assets/icons/campaignsHubIconLightNew.png';
import haloMMMIconLight from '../assets/icons/haloMMMIconLight.svg';
import myIntegrationsIconLightNew from '../assets/icons/myIntegrationsIconLight.svg';

// Nav icons — active state
import dashboardIconActive from '../assets/icons/dashboardIconActive.svg';
import askHaloIconActive from '../assets/icons/askHaloIconLightActive.svg';
import ordersIconActive from '../assets/icons/ordersIconActive.svg';
import campaignsHubIconActive from '../assets/icons/campaignsHubIconActive.svg';
import haloMMMIconActive from '../assets/icons/haloMMMIconActive.svg';
import myIntegrationsIconActive from '../assets/icons/integrationsIconActive.svg';

// Reports icons
import reportsIconDark from '../assets/custom-icons/reportsIconDark.svg';
import reportsIconLight from '../assets/custom-icons/reportsIconLight.svg';
import reportsIconActive from '../assets/custom-icons/reportsIconActive.svg';

// Calendar icons
import calendarIconDark from '../assets/custom-icons/calendarIconDark.svg';
import calendarIconLight from '../assets/custom-icons/calendarIconLight.svg';
import calendarIconActive from '../assets/custom-icons/calendarIconActive.svg';

interface NavItem {
  section: string;
  text: string;
  link: string;
  icon: string;
  lightIcon: string;
  lightIconActive: string;
  badge?: string;
  isLocked?: boolean;
}

const navItems: NavItem[] = [
  {
    section: 'Dashboard',
    text: 'Dashboard',
    icon: dashboardIconDark,
    lightIcon: dashboardIconLight,
    lightIconActive: dashboardIconActive,
    link: '/',
  },
  {
    section: 'AskHalo',
    text: 'Ask Halo',
    icon: askHaloIconDark,
    lightIcon: askHaloIconLight,
    lightIconActive: askHaloIconActive,
    link: '/ask-halo',
  },
  {
    section: 'Orders',
    text: 'Orders / Profitability',
    icon: ordersIconDarkNew,
    lightIcon: ordersIconDark,
    lightIconActive: ordersIconActive,
    link: '/orders',
  },
  {
    section: 'CampaignsHub',
    text: 'Campaigns Hub',
    icon: campaignsHubIconDarkNew,
    lightIcon: campaignsHubIconLightNew,
    lightIconActive: campaignsHubIconActive,
    link: '/campaigns',
  },
  {
    section: 'HaloEffectV2',
    text: 'Halo Effect',
    badge: 'Beta',
    icon: haloMMMIconDark,
    lightIcon: haloMMMIconLight,
    lightIconActive: haloMMMIconActive,
    link: '/halo-effect-v2',
  },
  {
    section: 'Calendar',
    text: 'Calendar',
    icon: calendarIconDark,
    lightIcon: calendarIconLight,
    lightIconActive: calendarIconActive,
    link: '/calendar',
  },
  {
    section: 'Reports',
    text: 'Reports',
    badge: 'Beta',
    icon: reportsIconDark,
    lightIcon: reportsIconLight,
    lightIconActive: reportsIconActive,
    link: '/reports',
  },
];

function NavTooltipWrapper({
  label,
  show,
  children,
}: {
  label: string;
  show: boolean;
  children: React.ReactNode;
}) {
  if (!show) return <>{children}</>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

interface DesktopSidebarProps {
  onSwitchStore: () => void;
  onLogout: () => void;
}

export function DesktopSidebar({ onSwitchStore, onLogout }: DesktopSidebarProps) {
  const { isDarkMode } = useDarkMode();
  const { storeId } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);

  const storeName = storeId ? localStorage.getItem('halo_store_name') : null;

  const isActive = (path: string) => {
    const pathname = path.split(/[?#]/)[0] ?? path;
    const hasHash = path.includes('#');
    const hasQuery = path.includes('?');

    if (pathname === '/' && location.pathname !== '/') return false;
    if (pathname !== '/' && !location.pathname.startsWith(pathname)) return false;

    if (hasHash) {
      const hash = '#' + path.split('#')[1];
      return location.hash === hash;
    }

    if (hasQuery) {
      const search = path.split('?')[1]?.split('#')[0];
      return search ? location.search.includes(search) : false;
    }

    return true;
  };

  const getIcon = (item: NavItem) => {
    if (isActive(item.link)) {
      return item.lightIconActive;
    }
    return isDarkMode ? item.icon : item.lightIcon;
  };

  const handleNavigation = (e: React.MouseEvent, link: string) => {
    const hashIndex = link.indexOf('#');
    if (hashIndex !== -1) {
      const pathPart = link.substring(0, hashIndex) || location.pathname;
      const hash = link.substring(hashIndex);

      if (location.pathname === pathPart) {
        e.preventDefault();
        navigate(link, { replace: true });
        const el = document.querySelector(hash);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="hidden md:flex">
        <div
          className={`h-screen relative border-r border-solid border-grey-50 dark:border-grey-500 ${
            open ? 'w-60' : 'w-20'
          } transition-[width] duration-300 sticky top-0 flex flex-col`}
          style={{
            zIndex: 40,
            backgroundColor: isDarkMode ? '#222222' : 'white',
          }}
        >
          {/* Header */}
          <div className="flex flex-col pt-4">
            <div className={`px-5 flex items-center ${!open && 'justify-center'}`}>
              {/* Full logo (open) */}
              <div
                className={`text-grey-800 dark:text-white text-2xl font-medium transition-all duration-300 flex items-center gap-2
                  ${open ? 'opacity-100 w-auto' : 'opacity-0 w-0 overflow-hidden'}
                `}
              >
                <img
                  src={isDarkMode ? iconTextLogoDark : iconTextLogoLight}
                  alt="Halo"
                  className="h-8"
                />
              </div>

              {/* Icon logo (collapsed) */}
              <div
                className={`transition-all duration-300
                  ${!open ? 'opacity-100 w-auto' : 'opacity-0 w-0 overflow-hidden'}
                `}
              >
                <img src={iconLogo} alt="Halo" className="w-8 h-8" />
              </div>
            </div>

            <div className="w-full h-[1px] my-4 dark:bg-charcoalGray-500 bg-charcoalGray-50" />
            <p
              className={`transition duration ease-in mb-4 text-[10px]
                text-charcoalGray-500 dark:text-white 
                ${open ? 'text-left pl-7' : 'text-center'}`}
            >
              MAIN
            </p>
          </div>

          {/* Floating border button — always visible */}
          <button
            className="absolute right-0 top-12 translate-x-1/2 z-50
              flex items-center justify-center
              w-7 h-7 rounded-full
              bg-white dark:bg-charcoalGray-600
              border border-grey-50 dark:border-charcoalGray-300
              shadow-md hover:shadow-lg hover:cursor-pointer
            "
            onClick={() => setOpen((prev) => !prev)}
          >
            <ChevronsLeftRight className="w-4 h-4 text-charcoalGray-500 dark:text-white" />
          </button>

          {/* Scrollable Nav */}
          <div className="flex-1 overflow-y-auto pt-0">
            <div className="flex flex-col text-left">
              {navItems.map((item) => (
                <div key={item.section} className="px-5">
                  <NavTooltipWrapper label={item.text} show={!open}>
                    <NavLink
                      to={item.link}
                      className={`group
                        flex my-1 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-500/40
                        p-2 transition duration-200
                        ${open ? 'justify-start items-center' : 'justify-center items-center'}
                        ${
                          isActive(item.link)
                            ? 'bg-primary-100 dark:bg-primary-500/20 text-charcoalGray-800 dark:text-white'
                            : 'bg-transparent text-charcoalGray-500 dark:text-charcoalGray-200 hover:text-charcoalGray-800 hover:dark:text-white'
                        }
                      `}
                      onClick={(e) => handleNavigation(e, item.link)}
                    >
                      <div className="flex items-center">
                        <img
                          src={getIcon(item)}
                          alt=""
                          className={`flex group-hover:hidden w-5 h-5 transition-all duration-300 ${open ? 'mr-2' : ''}`}
                        />
                        <img
                          src={item.lightIconActive}
                          alt=""
                          className={`hidden group-hover:flex w-5 h-5 transition-all duration-300 ${open ? 'mr-2' : ''}`}
                        />
                        <p
                          className={`text-[14px] flex transition-all duration-300 ease-in whitespace-nowrap items-center gap-1.5 ${open ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}
                        >
                          {item.text}
                          {item.badge && (
                            <span
                              className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                                isDarkMode
                                  ? 'bg-primary-500/15 text-primary-400'
                                  : 'bg-primary-50 text-primary-600'
                              }`}
                            >
                              {item.badge}
                            </span>
                          )}
                          {item.isLocked && (
                            <Lock className="w-3.5 h-3.5 text-charcoalGray-400 dark:text-charcoalGray-300" />
                          )}
                        </p>
                      </div>
                    </NavLink>
                  </NavTooltipWrapper>
                </div>
              ))}

              <div className="border-t border-grey-50 dark:border-charcoalGray-500 mt-2">
                {/* Settings section label */}
                <p
                  className={`text-[10px] text-charcoalGray-500 dark:text-white my-4  ${
                    open ? 'text-left  pl-7' : 'text-center'
                  }`}
                >
                  SETTINGS
                </p>
                {/* Integrations */}
                <div className="px-5 ">
                  <NavTooltipWrapper label="Integrations" show={!open}>
                    <NavLink
                      to="/integrations"
                      className={`group flex my-1 rounded-lg hover:bg-primary-200 dark:hover:bg-primary-500/40
                      p-2 transition duration-200
                      ${open ? 'justify-start items-center' : 'justify-center items-center'}
                      ${
                        isActive('/integrations')
                          ? 'bg-primary-100 dark:bg-primary-500/20 text-charcoalGray-800 dark:text-white'
                          : 'bg-transparent text-charcoalGray-500 dark:text-charcoalGray-200 hover:text-charcoalGray-800 hover:dark:text-white'
                      }
                    `}
                    >
                      <img
                        src={
                          isActive('/integrations')
                            ? myIntegrationsIconActive
                            : isDarkMode
                              ? myIntegrationsIcon
                              : myIntegrationsIconLightNew
                        }
                        alt=""
                        className={`flex group-hover:hidden w-5 h-5 transition-all duration-300 ${open ? 'mr-2' : ''}`}
                      />
                      <img
                        src={myIntegrationsIconActive}
                        alt=""
                        className={`hidden group-hover:flex w-5 h-5 transition-all duration-300 ${open ? 'mr-2' : ''}`}
                      />
                      <p
                        className={`text-[14px] flex transition-all duration-300 ease-in whitespace-nowrap ${open ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}
                      >
                        Integrations
                      </p>
                    </NavLink>
                  </NavTooltipWrapper>
                </div>

                {/* Store Settings */}
                <div className="px-5">
                  <NavTooltipWrapper label="Store Settings" show={!open}>
                    <NavLink
                      to="/settings"
                      className={`group flex my-1 rounded-lg hover:bg-primary-200
                      dark:hover:bg-primary-500/40 p-2 transition duration-200
                      ${open ? 'justify-start items-center' : 'justify-center items-center'}
                      ${
                        isActive('/settings')
                          ? 'bg-primary-100 dark:bg-primary-500/20 text-charcoalGray-800 dark:text-white'
                          : 'bg-transparent text-charcoalGray-500 dark:text-charcoalGray-200 hover:text-charcoalGray-800 hover:dark:text-white'
                      }
                    `}
                    >
                      <Settings
                        className={`w-5 h-5 transition-all duration-300 ${open ? 'mr-2' : ''} ${
                          isActive('/settings')
                            ? 'text-primary-500'
                            : 'text-charcoalGray-500 dark:text-charcoalGray-200 group-hover:text-primary-500'
                        }`}
                      />
                      <p
                        className={`text-[14px] flex transition-all duration-300 ease-in whitespace-nowrap ${open ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}
                      >
                        Store Settings
                      </p>
                    </NavLink>
                  </NavTooltipWrapper>
                </div>

                {/* My Account */}
                <div className="px-5">
                  <NavTooltipWrapper label="My Account" show={!open}>
                    <NavLink
                      to="/account"
                      className={`group flex my-1 rounded-lg hover:bg-primary-200
                      dark:hover:bg-primary-500/40 p-2 transition duration-200
                      ${open ? 'justify-start items-center' : 'justify-center items-center'}
                      ${
                        isActive('/account')
                          ? 'bg-primary-100 dark:bg-primary-500/20 text-charcoalGray-800 dark:text-white'
                          : 'bg-transparent text-charcoalGray-500 dark:text-charcoalGray-200 hover:text-charcoalGray-800 hover:dark:text-white'
                      }
                    `}
                    >
                      <UserCircle
                        className={`w-5 h-5 transition-all duration-300 ${open ? 'mr-2' : ''} ${
                          isActive('/account')
                            ? 'text-primary-500'
                            : 'text-charcoalGray-500 dark:text-charcoalGray-200 group-hover:text-primary-500'
                        }`}
                      />
                      <p
                        className={`text-[14px] flex transition-all duration-300 ease-in whitespace-nowrap ${open ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}
                      >
                        My Account
                      </p>
                    </NavLink>
                  </NavTooltipWrapper>
                </div>
              </div>
            </div>
          </div>

          {/* Fixed Footer */}
          <div className="flex flex-col gap-3">
            <div
              className={`flex flex-row w-full items-center gap-2 ${
                open ? 'justify-start px-5' : 'justify-center px-5'
              }`}
            >
              <NavTooltipWrapper
                label={isDarkMode ? 'Toggle Light mode' : 'Toggle Dark mode'}
                show={!open}
              >
                <div className="w-full">
                  <ModeColorSwitch isOpen={open} />
                </div>
              </NavTooltipWrapper>
            </div>

            {/* Active Store + Switch */}
            <div className="px-5 w-full">
              {storeName && (
                <div
                  className={`flex items-center gap-2 px-3 text-xs text-charcoalGray-500 dark:text-charcoalGray-300 truncate transition-all duration-300 ${open ? 'opacity-100 max-h-10 pb-2' : 'opacity-0 max-h-0 overflow-hidden pb-0'}`}
                >
                  <Store className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">{storeName}</span>
                </div>
              )}
              <NavTooltipWrapper label="Switch Store" show={!open}>
                <div
                  className={`
                    w-full flex p-2 items-center rounded-lg
                    bg-primary-100 dark:bg-primary-500/20
                    text-primary-700 dark:text-primary-400 text-sm font-medium
                    hover:cursor-pointer hover:bg-primary-200
                    hover:dark:bg-primary-500/30
                    ${!open && 'justify-center'}
                  `}
                  onClick={onSwitchStore}
                >
                  <div
                    className={`h-4 w-4 flex justify-center items-center flex-shrink-0 transition-all duration-300 ${open ? 'mr-2' : ''}`}
                  >
                    <ArrowLeftRight className="w-4 h-4" />
                  </div>
                  <span
                    className={`transition-all duration-300 whitespace-nowrap ${open ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}
                  >
                    Switch Store
                  </span>
                </div>
              </NavTooltipWrapper>
            </div>

            {/* Log Out */}
            <div className="px-5 pb-5 w-full h-full">
              <NavTooltipWrapper label="Log Out" show={!open}>
                <div
                  className={`
                    w-full h-full flex p-2 items-center rounded-lg
                    bg-warmSand-100 dark:bg-charcoalGray-900
                    text-charcoalGray-500 dark:text-charcoalGray-200 text-sm font-medium
                    hover:cursor-pointer hover:text-[#BD1616] hover:bg-warmSand-300
                    hover:dark:bg-charcoalGray-800 hover:dark:text-[#BD1616]
                    ${!open && 'justify-center'}
                  `}
                  onClick={onLogout}
                >
                  <div
                    className={`h-4 w-4 flex justify-center items-center flex-shrink-0 transition-all duration-300 ${open ? 'mr-2' : ''}`}
                  >
                    <LogOut className="w-4 h-4" />
                  </div>
                  <span
                    className={`transition-all duration-300 whitespace-nowrap ${open ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden'}`}
                  >
                    Log Out
                  </span>
                </div>
              </NavTooltipWrapper>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
